import { useEffect, useState } from 'react';
import api from '../../services/api';

interface Match {
    identifier: string; match_type: string; p2p_platform: string | null;
    p2p_name: string | null; tg_channel: string | null;
    p2p_first_seen?: string | null; p2p_profile_link?: string | null;
    p2p_source_ref?: string | null; telegram_source_ref?: string | null;
    telegram_observed_at?: string | null;
}

function profileUrl(value?: string | null) {
    try { const url = new URL(value || ''); return url.protocol === 'https:' ? url.href : null; }
    catch { return null; }
}
function messageUrl(reference?: string | null) {
    const match = reference?.match(/^([A-Za-z][A-Za-z0-9_]{3,31}):([1-9][0-9]*)$/);
    return match ? `https://t.me/${match[1]}/${match[2]}` : null;
}
const TYPES = [['all','All identifiers'],['phone','Phones'],['upi','UPI IDs'],['wallet','Wallets'],['telegram_handle','Telegram handles']];

export default function TelegramConnections({ refreshKey = 0 }: { refreshKey?: number }) {
    const [matches, setMatches] = useState<Match[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [type, setType] = useState('all');
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        const abort = new AbortController();
        setLoading(true); setError('');
        api.get('/p2p/cross-source-identities', { params: { limit: 200, match_type: type }, signal: abort.signal })
            .then(r => { if (!abort.signal.aborted) setMatches(r.data.matches || []); })
            .catch(() => { if (!abort.signal.aborted) { setMatches([]); setError('Connections could not be loaded. Match counts are unavailable.'); } })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [type, revision, refreshKey]);
    return <section className="space-y-3 p-4 sm:p-6" aria-label="Telegram and P2P connections">
        <h2 className="text-sm font-semibold">Shared identifiers between exchange ads and Telegram</h2>
        <p className="text-xs text-muted-foreground">Matches require the same canonical identifier in both sources and a retained exchange advertisement. They are investigation leads; common ownership is unverified. Phone matches preserve the shared identifier format, including foreign country codes.</p>
        <div className="flex flex-wrap items-center gap-3 text-xs">
            <label>Identifier type <select aria-label="Connection identifier type" value={type} onChange={e => setType(e.target.value)} className="ml-2 min-h-9 rounded border border-border bg-background px-2">
                {TYPES.map(([value,label]) => <option key={value} value={value}>{label}</option>)}
            </select></label>
            <button type="button" disabled={loading} onClick={() => setRevision(r => r+1)} className="min-h-9 rounded border border-border px-3">{error ? 'Retry connections' : 'Refresh connections'}</button>
            {!loading && !error && <span>{matches.length} matching identifiers{matches.length === 200 ? ' (first 200)' : ''}</span>}
        </div>
        {loading && <p role="status">Loading connections...</p>}
        {error && <p role="alert" className="text-destructive">{error}</p>}
        {!loading && !error && matches.length === 0 && <p className="text-sm text-muted-foreground">No shared identifier matches in the retained sources for this filter.</p>}
        {!loading && !error && matches.length > 0 && <div className="overflow-x-auto" tabIndex={0} aria-label="Connection evidence table"><table className="min-w-[640px] w-full text-left text-xs"><thead><tr>
            <th className="p-2">Identifier</th><th className="p-2">Exchange / advertiser</th><th className="p-2">Telegram evidence</th><th className="p-2">Observed ad</th>
        </tr></thead><tbody>{matches.map((r,i) => {
            const telegram = messageUrl(r.telegram_source_ref), profile = profileUrl(r.p2p_profile_link);
            return <tr key={`${r.match_type}-${r.identifier}-${i}`} className="border-t border-border align-top">
                <td className="p-2 font-mono break-all">{r.identifier}<span className="block font-sans text-muted-foreground">{r.match_type} - shared canonical identifier</span></td>
                <td className="p-2">{r.p2p_platform || 'Unspecified'} / {r.p2p_name || 'Unnamed'}
                    {r.p2p_source_ref && <span className="block text-muted-foreground">Ad {r.p2p_source_ref}</span>}
                    {profile && <a href={profile} target="_blank" rel="noopener noreferrer" className="block text-primary underline">Advertiser profile</a>}
                </td>
                <td className="p-2">{telegram ? <a href={telegram} target="_blank" rel="noopener noreferrer" className="text-primary underline">{r.telegram_source_ref}</a> : r.tg_channel || 'Source reference unavailable'}
                    {r.telegram_observed_at && <span className="block text-muted-foreground">{new Date(r.telegram_observed_at).toLocaleString('en-IN')}</span>}
                </td>
                <td className="p-2 whitespace-nowrap">{r.p2p_first_seen ? new Date(r.p2p_first_seen).toLocaleString('en-IN') : 'Date unavailable'}</td>
            </tr>;
        })}</tbody></table></div>}
    </section>;
}
